import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";
import { useRouteTitles } from "@/hooks/useRouteTitles";
import { usePlatformAnalytics } from "@/hooks/usePlatformAnalytics";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import FeatureGuard from "./components/FeatureGuard";
import MenuDigital from "./pages/MenuDigital";
import Login from "./pages/Login";
import Cadastro from "./pages/Cadastro";
import GerenciarCardapio from "./pages/GerenciarCardapio";
import PainelCozinha from "./pages/PainelCozinha";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Pedidos from "./pages/Pedidos";
import Clientes from "./pages/Clientes";
import Estoque from "./pages/Estoque";
import RelatorioCustos from "./pages/RelatorioCustos";
import Relatorios from "./pages/Relatorios";
import Fidelidade from "./pages/Fidelidade";
import MinhaConta from "./pages/MinhaConta";
import DeliveryPage from "./pages/Delivery";
import MesasAtivas from "./pages/MesasAtivas";
import AcompanharPedido from "./pages/AcompanharPedido";
import PDV from "./pages/PDV";
import IFoodIntegration from "./pages/IFoodIntegration";
import InstagramShopIntegration from "./pages/InstagramShopIntegration";
import WhatsAppCatalogIntegration from "./pages/WhatsAppCatalogIntegration";
import Marketing from "./pages/Marketing";
import Financeiro from "./pages/Financeiro";
import Funcionarios from "./pages/Funcionarios";
import GarcomLogin from "./pages/GarcomLogin";
import GarcomPDV from "./pages/GarcomPDV";
import LandingAds from "./pages/LandingAds";
import ChatRestaurante from "./pages/ChatRestaurante";
import SeoCampaignPage from "./pages/SeoCampaignPage";
import Crescimento from "./pages/Crescimento";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";

// Institutional pages
import TermosDeUso from "./pages/institutional/TermosDeUso";
import PoliticaPrivacidade from "./pages/institutional/PoliticaPrivacidade";
import Funcionalidades from "./pages/institutional/Funcionalidades";
import Integracoes from "./pages/institutional/Integracoes";
import ApiDocs from "./pages/institutional/ApiDocs";
import Contato from "./pages/institutional/Contato";
import CentralAjuda from "./pages/institutional/CentralAjuda";
import Documentacao from "./pages/institutional/Documentacao";
import Status from "./pages/institutional/Status";
import LojaDemo from "./pages/institutional/LojaDemo";
import Tutoriais from "./pages/institutional/Tutoriais";
import Changelog from "./pages/Changelog";
import Planos from "./pages/Planos";

// Affiliate pages
import AfiliadoLogin from "./pages/afiliado/AfiliadoLogin";
import AfiliadoCadastro from "./pages/afiliado/AfiliadoCadastro";
import AfiliadoDashboard from "./pages/afiliado/AfiliadoDashboard";
import ParceiroLanding from "./pages/afiliado/ParceiroLanding";

// Configuracoes sub-pages
import ConfiguracoesLayout from "./pages/configuracoes/ConfiguracoesLayout";
import InformacoesGerais from "./pages/configuracoes/InformacoesGerais";
import Aparencia from "./pages/configuracoes/Aparencia";
import Endereco from "./pages/configuracoes/Endereco";
import Horario from "./pages/configuracoes/Horario";
import RedesSociais from "./pages/configuracoes/RedesSociais";
import DeliveryConfig from "./pages/configuracoes/DeliveryConfig";
import Pagamento from "./pages/configuracoes/Pagamento";
import QrCodes from "./pages/configuracoes/QrCodes";
import PopupsPromo from "./pages/configuracoes/PopupsPromo";
import AnalyticsConfig from "./pages/configuracoes/AnalyticsConfig";
import Faturamento from "./pages/configuracoes/Faturamento";
import WhatsAppConfig from "./pages/configuracoes/WhatsAppConfig";

const queryClient = new QueryClient();

import PublicChatWidget from "@/components/PublicChatWidget";
import AutoSeoHead from "@/components/AutoSeoHead";

const PageTracker = () => { usePageTracking(); useDynamicFavicon(); usePlatformAnalytics(); useRouteTitles(); return null; };

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="viamenu-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
        <PageTracker />
        <AutoSeoHead />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/menu" element={<MenuDigital />} />
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<Cadastro />} />
          <Route path="/gerenciar-cardapio" element={<FeatureGuard featureKey="menu_digital"><GerenciarCardapio /></FeatureGuard>} />
          <Route path="/cozinha" element={<FeatureGuard featureKey="kitchen_panel"><PainelCozinha /></FeatureGuard>} />
          <Route path="/pedidos" element={<Pedidos />} />
          <Route path="/clientes" element={<FeatureGuard featureKey="customer_management"><Clientes /></FeatureGuard>} />
          <Route path="/estoque" element={<FeatureGuard featureKey="stock_management"><Estoque /></FeatureGuard>} />
          <Route path="/relatorio-custos" element={<FeatureGuard featureKey="cost_reports"><RelatorioCustos /></FeatureGuard>} />
          <Route path="/relatorios" element={<FeatureGuard featureKey="advanced_reports"><Relatorios /></FeatureGuard>} />
          <Route path="/fidelidade" element={<FeatureGuard featureKey="loyalty_program"><Fidelidade /></FeatureGuard>} />
          <Route path="/minhas-recompensas" element={<MinhaConta />} />
          <Route path="/minha-conta" element={<MinhaConta />} />
          <Route path="/delivery" element={<FeatureGuard featureKey="delivery_management"><DeliveryPage /></FeatureGuard>} />
          <Route path="/acompanhar" element={<AcompanharPedido />} />
          <Route path="/mesas" element={<FeatureGuard featureKey="table_management"><MesasAtivas /></FeatureGuard>} />
          <Route path="/pdv" element={<FeatureGuard featureKey="pdv"><PDV /></FeatureGuard>} />
          <Route path="/ifood" element={<FeatureGuard featureKey="ifood_integration"><IFoodIntegration /></FeatureGuard>} />
          <Route path="/instagram-shop" element={<InstagramShopIntegration />} />
          <Route path="/whatsapp-catalogo" element={<WhatsAppCatalogIntegration />} />
          <Route path="/marketing" element={<FeatureGuard featureKey="marketing_ai"><Marketing /></FeatureGuard>} />
          <Route path="/crescimento" element={<FeatureGuard featureKey="growth_mode"><Crescimento /></FeatureGuard>} />
          <Route path="/chat-clientes" element={<ChatRestaurante />} />
          <Route path="/financeiro" element={<FeatureGuard featureKey="financial_management"><Financeiro /></FeatureGuard>} />
           <Route path="/funcionarios" element={<FeatureGuard featureKey="staff_management"><Funcionarios /></FeatureGuard>} />
           <Route path="/garcom/login" element={<GarcomLogin />} />
           <Route path="/garcom" element={<GarcomPDV />} />
           <Route path="/admin/login" element={<AdminLogin />} />
           <Route path="/admin" element={<AdminDashboard />} />

          {/* Institutional */}
          <Route path="/funcionalidades" element={<Funcionalidades />} />
          <Route path="/integracoes" element={<Integracoes />} />
          <Route path="/api" element={<ApiDocs />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/ajuda" element={<CentralAjuda />} />
          <Route path="/documentacao" element={<Documentacao />} />
          <Route path="/status" element={<Status />} />
          <Route path="/termos" element={<TermosDeUso />} />
          <Route path="/privacidade" element={<PoliticaPrivacidade />} />
          <Route path="/demo" element={<Navigate to="/lojademo" replace />} />
          <Route path="/tutoriais" element={<Tutoriais />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/planos" element={<Planos />} />
          <Route path="/lp" element={<LandingAds />} />
          <Route path="/s/:slug" element={<SeoCampaignPage />} />

          {/* Affiliate Portal */}
          <Route path="/afiliado/login" element={<AfiliadoLogin />} />
          <Route path="/afiliado/cadastro" element={<AfiliadoCadastro />} />
          <Route path="/afiliado" element={<AfiliadoDashboard />} />
          <Route path="/parceiro/:code" element={<ParceiroLanding />} />

          <Route path="/configuracoes" element={<ConfiguracoesLayout />}>
            <Route index element={<InformacoesGerais />} />
            <Route path="aparencia" element={<Aparencia />} />
            <Route path="endereco" element={<Endereco />} />
            <Route path="horario" element={<Horario />} />
             <Route path="redes-sociais" element={<RedesSociais />} />
             <Route path="analytics" element={<AnalyticsConfig />} />
            <Route path="delivery" element={<DeliveryConfig />} />
            <Route path="pagamento" element={<Pagamento />} />
            <Route path="qr-codes" element={<QrCodes />} />
            <Route path="popups" element={<PopupsPromo />} />
            <Route path="faturamento" element={<Faturamento />} />
            <Route path="whatsapp" element={<WhatsAppConfig />} />
          </Route>
          <Route path="/install" element={<Install />} />
          <Route path="/:slug" element={<MenuDigital />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <PublicChatWidget />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
